/**
 * Scroll Debug Utility
 * 
 * Utility functions to help debug and monitor scroll behavior
 * Created: June 2025 to assist with scroll-to-bottom troubleshooting
 */

export interface ScrollPosition {
  x: number;
  y: number;
  timestamp: number;
  method: string;
}

export class ScrollDebugger {
  private positions: ScrollPosition[] = [];
  private isLogging = false;
  
  /**
   * Start logging scroll positions
   */
  startLogging() {
    if (this.isLogging) return;
    
    this.isLogging = true;
    this.positions = [];
    
    console.log('[ScrollDebugger] Started logging scroll positions');
    
    // Log initial position
    this.logPosition('initial');
    
    // Monitor scroll events
    window.addEventListener('scroll', this.onScroll);
    
    // Monitor programmatic scrolls
    const originalScrollTo = window.scrollTo.bind(window);
    (window as any).scrollTo = (optionsOrX?: ScrollToOptions | number, y?: number) => {
      this.logPosition('programmatic-scrollTo');
      if (typeof optionsOrX === 'number' && y !== undefined) {
        return originalScrollTo(optionsOrX, y);
      } else if (optionsOrX) {
        return originalScrollTo(optionsOrX as ScrollToOptions);
      } else {
        return originalScrollTo();
      }
    };
  }
  
  /**
   * Stop logging scroll positions
   */
  stopLogging() {
    if (!this.isLogging) return;
    
    this.isLogging = false;
    window.removeEventListener('scroll', this.onScroll);
    
    console.log('[ScrollDebugger] Stopped logging. Total positions recorded:', this.positions.length);
    this.printSummary();
  }
  
  /**
   * Get current scroll position
   */
  getCurrentPosition(): ScrollPosition {
    return {
      x: window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0,
      y: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0,
      timestamp: Date.now(),
      method: 'current'
    };
  }
  
  /**
   * Log scroll position with method
   */
  private logPosition(method: string) {
    const position = this.getCurrentPosition();
    position.method = method;
    
    this.positions.push(position);
    
    if (position.y > 10) {
      console.warn(`[ScrollDebugger] Unwanted scroll detected: Y=${position.y} via ${method}`);
    }
  }
  
  /**
   * Handle scroll events
   */
  private onScroll = () => {
    this.logPosition('scroll-event');
  }
  
  /**
   * Print summary of scroll positions
   */
  printSummary() {
    console.group('[ScrollDebugger] Scroll Position Summary');
    
    console.log('Total positions recorded:', this.positions.length);
    
    const maxY = Math.max(...this.positions.map(p => p.y));
    const finalY = this.positions[this.positions.length - 1]?.y || 0;
    
    console.log('Maximum Y position reached:', maxY);
    console.log('Final Y position:', finalY);
    
    // Find all positions where Y > 10 (unwanted scrolling)
    const unwantedScrolls = this.positions.filter(p => p.y > 10);
    if (unwantedScrolls.length > 0) {
      console.warn('Unwanted scroll events detected:', unwantedScrolls.length);
      console.table(unwantedScrolls.map(p => ({
        Y: p.y,
        Method: p.method,
        Time: new Date(p.timestamp).toLocaleTimeString()
      })));
    } else {
      console.log('✅ No unwanted scrolling detected');
    }
    
    console.groupEnd();
  }
  
  /**
   * Get all logged positions
   */
  getPositions(): ScrollPosition[] {
    return [...this.positions];
  }
  
  /**
   * Clear logged positions
   */
  clearPositions() {
    this.positions = [];
    console.log('[ScrollDebugger] Cleared position history');
  }
}

// Create global instance
export const scrollDebugger = new ScrollDebugger();

// Add to window for easy access in browser console
if (typeof window !== 'undefined') {
  (window as any).scrollDebugger = scrollDebugger;
  (window as any).startScrollLogging = () => scrollDebugger.startLogging();
  (window as any).stopScrollLogging = () => scrollDebugger.stopLogging();
  (window as any).checkScrollPosition = () => {
    const pos = scrollDebugger.getCurrentPosition();
    console.log('Current scroll position:', pos);
    return pos;
  };
}
